/**
 * Business hours parsing + open/closed check.
 *
 * The widget stores working-hours as human-readable strings, one per
 * weekday (mon / tue / wed / thu / fri / sat / sun). Example inputs:
 *
 *   "Monday: 9:00 - 18:00"
 *   "Saturday: 10:00 - 15:00"
 *   "Sunday: Closed"
 *   "11:00 - 16:00"                      // bare range, no day label
 *   "20:00 - 02:00"                      // overnight range
 *
 * We extract the `HH:MM - HH:MM` range and compare against the user's
 * local clock. This means the check runs in the VIEWER's timezone — a
 * reasonable approximation since most customers browse a local dealer,
 * and we don't store the dealer's timezone anywhere.
 *
 * `isBusinessOpen()` returns:
 *   true  — current time is within today's range
 *   false — either outside range, or today is "Closed" / unparseable
 *
 * Overnight hours (e.g. "20:00 - 02:00") are supported: when the parsed
 * end minutes are less than the start minutes, the window is treated as
 * wrapping past midnight.
 */

// Matches "9:00 - 18:00", "09:00–18:00", "9:00 -18:00", etc.
// Supports ASCII hyphen (-) and en-dash (–).
const TIME_RANGE_RE = /(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/;

/**
 * Parse a single hours string → { startMinutes, endMinutes } or null.
 *
 * @param {string|null|undefined} str
 * @returns {{ startMinutes: number, endMinutes: number } | null}
 */
export function parseHoursRange(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.match(TIME_RANGE_RE);
  if (!m) return null;
  const startH = parseInt(m[1], 10);
  const startM = parseInt(m[2], 10);
  const endH = parseInt(m[3], 10);
  const endM = parseInt(m[4], 10);
  // Basic sanity — reject garbage like "99:99"
  if (startH > 23 || startM > 59 || endH > 24 || endM > 59) return null;
  return {
    startMinutes: startH * 60 + startM,
    endMinutes: endH * 60 + endM,
  };
}

/**
 * Pick today's hours string from a per-day bucket.
 *
 * @param {{ mon?: string, tue?: string, wed?: string, thu?: string, fri?: string, sat?: string, sun?: string }} hours
 * @param {Date} now
 * @returns {string | undefined}
 */
export function getTodayHours(hours, now) {
  if (!hours) return undefined;
  // Date.prototype.getDay() → 0 = Sunday, 1 = Monday, …, 6 = Saturday
  const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return hours[keys[now.getDay()]];
}

/**
 * Is the business currently open, given its advertised hours?
 *
 * Supports overnight hours (e.g. "20:00 - 02:00"): when end < start the
 * window is treated as wrapping past midnight.
 *
 * @param {{ mon?: string, tue?: string, wed?: string, thu?: string, fri?: string, sat?: string, sun?: string }} hours
 * @param {Date} [now=new Date()]  Override for tests.
 * @returns {boolean}
 */
export function isBusinessOpen(hours, now = new Date()) {
  const todayStr = getTodayHours(hours, now);
  const range = parseHoursRange(todayStr);
  if (!range) return false;
  const current = now.getHours() * 60 + now.getMinutes();
  // Overnight range (e.g. 20:00 - 02:00): open if current >= start OR current < end.
  if (range.endMinutes < range.startMinutes) {
    return current >= range.startMinutes || current < range.endMinutes;
  }
  return current >= range.startMinutes && current < range.endMinutes;
}
